/**
 * What a recipe is — T130, **D88**.
 *
 * A **recipe** turns a bag of items into one item, at a place. That is all of
 * it. `Development/Minecraft/04_Crafting_And_Progression.md` opens with a
 * verdict table, and the two lines that built this file are:
 * **recipe-as-declarative-data is the most load-bearing mechanic in the
 * document**, and **the 3×3 grid is flavour.**
 *
 * ## 🔴 Matching is COUNTING, not pattern-matching
 *
 * 🎯 **haugur, 2026-08-25 — D88:** you craft from **a list with pictures**, not
 * a grid. So {@link RecipeDef.inputs} is an unordered bag and
 * {@link canMake} counts. There is no key map, no translation, no horizontal
 * mirroring, and **no drag-and-drop screen to go with them**.
 *
 * ⚠️ **{@link RecipeDef.arrangement} is a PICTURE and is never read here.**
 * It is the half of the grid worth keeping — the campfire still *looks* like
 * four stones round a log, so the recipe teaches its own shape — and because
 * nothing matches against it, **it costs nothing to be wrong.** 🔴 **If a
 * future session finds itself reading `arrangement` in matching code, D88 has
 * been lost.**
 *
 * ## ⚠️ Tags, or the recipe count explodes
 *
 * `Minecraft/04` §3 marks ingredient tags load-bearing rather than convenient.
 * *Four stone* is three recipes today and thirty when Iceland's rocks are
 * finished. An input names **a tag or an item, never both**.
 *
 * ## 🔴 A recipe may name something that does not exist yet, and that is on purpose
 *
 * `items.json` already does this: the birch log's `leavesBehind` names charcoal,
 * charcoal has no row, **and a test asserts the absence.** The alternative is
 * inventing an item before anything can place it, which is exactly how a thing
 * ships unreachable without anybody saying so.
 *
 * So loading **never throws** for a missing item. It marks the recipe
 * {@link Recipe.reachable} `false` and records {@link Recipe.missing}, and
 * {@link canMake} refuses it. 👉 **The day the item arrives, the flag flips and
 * a test that asserted the absence turns red** — which is the point.
 *
 * ⚠️ **It DOES throw for a missing TAG**, and the difference is deliberate: a
 * tag that is not in this file is a typo, not a thing waiting to be built.
 */

import recipeData from '../data/recipes.json' with { type: 'json' };
import { itemIdForKey } from './items.ts';
import type { Stack } from './inventory.ts';

/**
 * One ingredient. **A tag or an item, never both** — the loader refuses both.
 */
export interface RecipeInput {
  readonly tag?: string;
  readonly item?: string;
  readonly count: number;
}

export interface RecipeResult {
  readonly item: string;
  readonly count: number;
}

/**
 * A recipe as it is authored in `recipes.json`.
 */
export interface RecipeDef {
  readonly key: string;
  /**
   * Where it can be made. 🔴 **`null` means in your hands.**
   *
   * `Minecraft/04` §12 makes *a recipe needs a place* load-bearing, and the
   * bench recipe is the one exception it has to have — **otherwise nothing can
   * ever be made.** Minecraft's own crafting table works this way.
   */
  readonly station: string | null;
  readonly inputs: readonly RecipeInput[];
  readonly result: RecipeResult;
  /**
   * 📖 **Hold one of these and you learn this recipe** — **D88**.
   *
   * `Minecraft/04` §10.6: Minecraft's recipes were taught by a community of
   * millions for six years. **We have a family, and nobody will write our
   * wiki.** One field, and it is the only teaching that grows by itself.
   */
  readonly unlockedBy: readonly string[];
  /**
   * 🖼️ **A picture, never a rule.** Drawn on the screen; never matched. `null`
   * is a hole in the arrangement.
   */
  readonly arrangement?: readonly (string | null)[];
  /**
   * 🖼️ **How wide the picture is, in cells.**
   *
   * 🔴 **The width is NOT guessable and drawing it wrong is silent.** The
   * hearth's six cells are *stone · wood · stone / stone · hole · stone* —
   * **three** columns, four stones round one log. The bench's four are a square
   * of boards — **two**. A single rule would draw one of them wrong, and
   * nothing would fail.
   *
   * ⚠️ **This does not make `arrangement` a rule.** It is still never matched
   * against; this only says how to lay the picture out, and D88's point stands:
   * **the picture costs nothing to be wrong.**
   */
  readonly arrangementCols?: number;
  /**
   * 🗂️ **Which tab of the craft screen this belongs on** — **D122, T249.**
   *
   * 🎯 **haugur, 2026-09-22, having lost his spear and not found the recipe:**
   * *"the scroll bara is not visible until you scroll, so you dont get the the
   * feeling you can scroll, i think we should do a multitab menu for crafting,
   * so fr instance having a tab for tools, a tab for house materials etc."*
   *
   * 🔴 **THE BUG IT ENDS: five rows fit and the bench had THIRTEEN recipes**, so
   * **eight were invisible** — every stone tool, the chest, all three houses,
   * and the spjót last of all. 📏 **Measured at four window sizes; the same five
   * at every one of them.**
   *
   * ⚠️ **THE INDICATOR HAD BEEN FIXED TWICE AND HE STILL COULD NOT SEE IT.** A
   * counter — *1–5 / 13* — and then a rail, both added after his own report of
   * **2026-09-02**: *"there is no visual indicator that you can scroll down the
   * list."* 👉 ***Two goes at the affordance failed, which is the argument that
   * the affordance was never the problem.*** 🔴 **And `06_Traps.md` already
   * carried it: *a list that does not fit must say so* — four of nine recipes
   * were invisible for a day and the screen looked finished. This is the same
   * fault, twice as big.**
   *
   * ✅ **So the fix is structural: no tab may hold more rows than fit**, and
   * `hud-scene.test.ts` asserts exactly that. **A fourteenth recipe that would
   * overflow a tab fails the build** rather than quietly hiding the thirteenth.
   */
  readonly category: RecipeCategory;
}

/**
 * 🗂️ **The tabs of the craft screen, in the order they are drawn** — D122.
 *
 * 🎯 **haugur's own four**, chosen over three when he was shown that seven tools
 * do not fit in five rows: *"Four tabs — split the tools."*
 *
 * ⚠️ **The ORDER here is the order on screen**, and it is the order you meet
 * them in: you saw wood before stone, and you build a house before you furnish
 * it. 🔴 **A tab with nothing in it at this station is not drawn at all**, so
 * pressing `E` shows two tabs and a bench shows four, from one list.
 */
/* 🎣 **`veidi` — fishing, D129, 2026-09-29.** A fifth tab at the bench, because
   the hook, the line and the rod put EIGHT rows in `heimili` where five fit —
   found by the test that exists for exactly this, before anybody shipped a
   recipe nobody could see. */
/* 🐑 **`bufe` — livestock, D130, 2026-09-29.** A sixth tab, and for the same
   reason: the stone wall and the pen gate put SEVEN rows in `heimili` where
   five fit. The lead and the tether peg moved with them — everything you keep
   sheep with is on one tab, and Home is the hearth, the torch and the chest. */
export const RECIPE_CATEGORIES = [
  'efni',
  'tre',
  'steinn',
  'hus',
  'heimili',
  'veidi',
  'bufe',
] as const;

export type RecipeCategory = (typeof RECIPE_CATEGORIES)[number];

/** A recipe after loading, with what the world can and cannot yet supply. */
export interface Recipe extends RecipeDef {
  /**
   * 🔴 **False means it CANNOT be made and the screen must not offer it.**
   * Something it names has no row in `items.json` yet.
   */
  readonly reachable: boolean;
  /** The item keys it names that do not exist. Empty when reachable. */
  readonly missing: readonly string[];
}

interface AuthoredTag {
  readonly items: readonly string[];
}

/* A Map rather than the plain object, and it is the house pattern rather than a
   preference: `items.ts` looks every key up through `BY_KEY`, and indexing an
   object with a string that came from a data file is what
   `security/detect-object-injection` exists to catch. */
const TAGS: ReadonlyMap<string, AuthoredTag> = new Map(
  Object.entries(recipeData.tags as Readonly<Record<string, AuthoredTag>>),
);

/**
 * The item keys a tag stands for.
 *
 * ⚠️ **Throws for an unknown tag.** A tag is written in this repo by us; a
 * missing one is a typo, not a thing waiting to be built. That is the opposite
 * of a missing ITEM, which is tolerated on purpose — see the file header.
 */
export function tagItems(tag: string): readonly string[] {
  const t = TAGS.get(tag);
  if (!t) {
    throw new Error(
      `recipes.json names the tag "${tag}" and no such tag is defined. ` +
        `Known tags: ${[...TAGS.keys()].join(', ')}`,
    );
  }
  return t.items;
}

/** Every item key an input will accept — one key for an item, several for a tag. */
export function inputAccepts(input: RecipeInput): readonly string[] {
  if (input.tag && input.item) {
    throw new Error(
      `A recipe input names both the tag "${input.tag}" and the item "${input.item}". ` +
        `It must name one or the other.`,
    );
  }
  if (input.tag) return tagItems(input.tag);
  if (input.item) return [input.item];
  throw new Error('A recipe input names neither a tag nor an item.');
}

function itemExists(key: string): boolean {
  return itemIdForKey(key) !== undefined;
}

export const RECIPES: readonly Recipe[] = (recipeData.recipes as readonly RecipeDef[]).map(
  (def) => {
    /* Resolving every input here rather than at match time means a typo in a tag
     name throws at STARTUP, the way a missing item id does in items.ts —
     rather than the first time somebody opens the bench. */
    const named = new Set<string>([def.result.item]);
    for (const input of def.inputs) for (const key of inputAccepts(input)) named.add(key);

    const missing = [...named].filter((key) => !itemExists(key));
    return Object.freeze({
      ...def,
      reachable: missing.length === 0,
      missing: Object.freeze(missing),
    });
  },
);

export const RECIPE: Readonly<Record<string, Recipe>> = Object.freeze(
  Object.fromEntries(RECIPES.map((r) => [r.key, r])),
);

/** Every recipe that can actually be made today. */
export function reachableRecipes(): readonly Recipe[] {
  return RECIPES.filter((r) => r.reachable);
}

/**
 * 🔨 **Which recipes a screen SHOWS** — the pack's craft page, or a station's.
 *
 * 🎯 **haugur, 2026-08-31, deciding what pressing `E` lists:** *"only hand, and
 * smíðabekkur show its menu and same for hearth."* 👉 **A station's menu belongs
 * to the station.** The same rule that made the hearth its own screen instead of
 * a third tab (T128), applied one step further back.
 *
 * **So the two lists never overlap:**
 *
 * | Opened by | `station` | Shows |
 * |---|---|---|
 * | `E` | `null` | Only what your **hands** can make |
 * | Right-clicking a bench | `'smidabekkur'` | Only **that bench's** recipes |
 *
 * 🔴 **This REVERSED half of itself the day it was written down.** Until
 * 2026-08-31 the pack listed bench recipes greyed out, on the argument that *"it
 * is what tells a new player why to build a bench at all"* — haugur ruled
 * against it, having been told what it costs.
 *
 * ⚠️ **What it costs, stated rather than buried:** nothing announces that
 * `eldstæði` exists until you stand at a bench. ✅ **D88 is the intended
 * answer** — *you learn a recipe when you first HOLD one of the things in it* —
 * ⚠️ **but that is slice 4 and is NOT built.** Until it is, the game teaches the
 * bench's list nowhere at all.
 *
 * 🔴 **This decides only what is DRAWN. It is not a permission check.** The
 * server derives the station from the block the player is standing at and
 * refuses with `wrong-station` regardless of what any screen listed — a client
 * that could name its own station could name a bench it is nowhere near.
 *
 * @param station The station being worked at, or `null` for bare hands.
 */
export function recipesShownAt(station: string | null): readonly Recipe[] {
  return reachableRecipes().filter((r) => r.station === station);
}

/**
 * 🗂️ **Which tabs this station has**, in {@link RECIPE_CATEGORIES}'s order —
 * D122, T249.
 *
 * 🔴 **Derived from the recipes, never listed.** A station shows a tab because
 * something it can make is in that category, so **a tab can never be empty** and
 * **a new recipe brings its tab with it.** ⚠️ A hard-coded list of tabs per
 * station is the second-way-in fault this repo names oftener than any other.
 */
export function recipeCategoriesAt(station: string | null): readonly RecipeCategory[] {
  const here = recipesShownAt(station);
  return RECIPE_CATEGORIES.filter((c) => here.some((r) => r.category === c));
}

/**
 * 🗂️ **What one tab of one station lists.**
 *
 * ⚠️ **This is what the screen draws, and `hud-scene.test.ts` asserts that no
 * answer it can give is longer than the rows that fit.** That assertion is the
 * whole point of the tabs: the fault it guards is a recipe that is present,
 * correct, and invisible.
 */
export function recipesInTab(station: string | null, category: RecipeCategory): readonly Recipe[] {
  return recipesShownAt(station).filter((r) => r.category === category);
}

/**
 * 📖 The recipes picking this item up teaches you — **D88**.
 */
export function recipesUnlockedBy(itemKey: string): readonly Recipe[] {
  return RECIPES.filter((r) => r.unlockedBy.includes(itemKey));
}

/** How many of an item a bag of stacks holds. */
function countIn(stacks: readonly Stack[], itemKey: string): number {
  let n = 0;
  for (const s of stacks) if (s.itemKey === itemKey) n += s.count;
  return n;
}

/** Why a recipe cannot be made right now. `null` when it can. */
export type RefusalReason = 'unreachable' | 'wrong-station' | 'short';

export interface MakeCheck {
  readonly ok: boolean;
  readonly reason: RefusalReason | null;
  /**
   * ⚠️ **The tags or items you are short of, and by how many.**
   *
   * 🔴 **The screen has to be able to SAY this.** *"I clicked it and nothing
   * happened"* is a bug report — the same rule D64 already applies to the cold
   * (*"Exposed — wind, no shelter"*) and `items.json` to eating at a ceiling.
   */
  readonly short: readonly { readonly of: string; readonly by: number }[];
}

/**
 * 🔴 **Can this be made, here, from these stacks?**
 *
 * @param station the bench you have open, or `null` for your bare hands.
 *
 * ⚠️ **Counting an input at a time is WRONG when two inputs share a tag** and
 * this deliberately does not pretend otherwise — see the test. No recipe today
 * has overlapping inputs; when one does, this needs to become an assignment
 * rather than a count, and **the test that proves it is already written and
 * skipped rather than absent.**
 */
export function canMake(
  recipe: Recipe,
  stacks: readonly Stack[],
  station: string | null,
): MakeCheck {
  if (!recipe.reachable) return { ok: false, reason: 'unreachable', short: [] };
  if (recipe.station !== station) return { ok: false, reason: 'wrong-station', short: [] };

  const short: { of: string; by: number }[] = [];
  for (const input of recipe.inputs) {
    const have = inputAccepts(input).reduce((n, key) => n + countIn(stacks, key), 0);
    if (have < input.count) short.push({ of: input.tag ?? input.item!, by: input.count - have });
  }
  return short.length
    ? { ok: false, reason: 'short', short: Object.freeze(short) }
    : { ok: true, reason: null, short: [] };
}

/** Everything you could make right now, at this station. */
export function whatCanIMake(stacks: readonly Stack[], station: string | null): readonly Recipe[] {
  return RECIPES.filter((r) => canMake(r, stacks, station).ok);
}

/**
 * 🖼️ **Which single item stands for a cell of a recipe's picture.**
 *
 * A cell — or a result — may name a **tag** or an **item**, and the screen has
 * to draw one face either way. A tag stands for several items; the picture
 * shows the **first**, which is the one a player is most likely to be holding.
 *
 * 🔴 **This exists because the crafting screen crashed on the live server on
 * 2026-08-27.** It called {@link tagItems} on `smidabekkur` — a recipe's
 * RESULT, which is an item and not a tag — and `tagItems` throws for an unknown
 * tag, which is correct of it. ⚠️ **Nothing looked broken:** the click landed on
 * the tab, the exception escaped the handler, and the page simply never redrew.
 * **The Craft tab did nothing at all and said nothing at all.**
 *
 * 👉 **The deeper fault was that the design preview used its own copy of this
 * logic rather than the shipped code**, so it could not have caught it — the
 * exact drift `hud-scene.ts` was moved into the client to prevent.
 */
export function pictureItemFor(key: string): string {
  const tag = TAGS.get(key);
  if (!tag) return key;
  return tag.items[0] ?? key;
}
